/**
 * Treasures chest logo loader for the card-back QR center.
 *
 * Fetches the brand chest SVG (`/icon.svg`), optionally recolors its
 * green palette to a pop-art combo so it pairs with the violet QR, then
 * rasterizes it to a cached square PNG. The PNG is layered over the QR
 * center in the DOM (with a white "well" punched into the QR) so the
 * mark stays crisp at print scale.
 *
 * Ported from the treasures.to Treasure Trolls print sheet.
 */

/**
 * Pop-art recolor map for the brand SVG. Keys are the original
 * lowercase hex values found in `/public/icon.svg`. Two of these
 * (#67cc6c, #4ace3d) appear only inside a `<style>` block as CSS
 * classes (.cls-3 / .cls-4), so the replace runs against the whole
 * document, not just `fill="..."` attributes.
 */
const POP_RECOLOR_MAP: Record<string, string> = {
  "#034e09": "#d6007e", // darkest green (outline) → magenta
  "#1d7721": "#0080a8", // dark green shadow → deep cyan
  "#3d9f35": "#00c8e0", // mid green → bright cyan
  "#4ace3d": "#ffdb1a", // bright green chest body → electric yellow
  "#67cc6c": "#ffe85c", // light green highlight → lighter yellow
  // Cream highlight (#eae9dd) is left alone — it reads as the glowing
  // gem in the chest and works against any backdrop.
};

let _svgCache: Record<"original" | "pop", string | undefined> = {
  original: undefined,
  pop: undefined,
};
let _pngCache: Record<"original" | "pop", string | undefined> = {
  original: undefined,
  pop: undefined,
};

async function loadChestSvg(recolor: boolean): Promise<string> {
  const variant = recolor ? "pop" : "original";
  const cached = _svgCache[variant];
  if (cached) return cached;

  const res = await fetch("/icon.svg");
  if (!res.ok) throw new Error(`Failed to fetch icon.svg: ${res.status}`);
  let svg = await res.text();

  if (recolor) {
    for (const [from, to] of Object.entries(POP_RECOLOR_MAP)) {
      svg = svg.replace(new RegExp(from, "gi"), to);
    }
  }

  // Inject explicit width/height on the root <svg> if missing — some
  // browsers refuse to rasterize an <svg> without intrinsic dimensions.
  if (!/<svg[^>]*\bwidth=/.test(svg)) {
    const vb = svg.match(/viewBox=["']([\d. -]+)["']/);
    let w = 512;
    let h = 512;
    if (vb) {
      const parts = vb[1].trim().split(/\s+/).map(Number);
      if (parts.length === 4) {
        w = parts[2];
        h = parts[3];
      }
    }
    svg = svg.replace("<svg", `<svg width="${w}" height="${h}"`);
  }

  _svgCache[variant] = svg;
  return svg;
}

/**
 * Rasterize the chest SVG into a square PNG data URL at the requested
 * pixel size. Cached per variant so we only pay for fetch+decode once.
 */
export async function loadChestPng(
  size: number,
  recolor: boolean,
): Promise<string> {
  const variant = recolor ? "pop" : "original";
  const cached = _pngCache[variant];
  if (cached) return cached;

  const svgSource = await loadChestSvg(recolor);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgSource)}`;

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Failed to decode chest SVG"));
    i.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  ctx.imageSmoothingEnabled = true;
  if ("imageSmoothingQuality" in ctx) {
    (ctx as unknown as Record<string, unknown>).imageSmoothingQuality = "high";
  }

  // Letterbox so the chest's natural aspect ratio is preserved inside
  // the square output.
  const srcW = img.naturalWidth || size;
  const srcH = img.naturalHeight || size;
  const scale = Math.min(size / srcW, size / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const dx = (size - drawW) / 2;
  const dy = (size - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);

  const png = canvas.toDataURL("image/png", 1.0);
  _pngCache[variant] = png;
  return png;
}

/** Synchronous read of the cached PNG (call after `loadChestPng`). */
export function getCachedChestPng(recolor: boolean): string | undefined {
  return _pngCache[recolor ? "pop" : "original"];
}
